import DOMPurify from 'dompurify';

interface ProcessTemplateOptions {
  content: string;
  data?: Record<string, string>;
  errorIconSrc: string;
  mappedValueClass: string;
  errorClass: string;
  isHtmlContent?: boolean; // false for SMS (plain text), true for Email
}

/** When true, {@link neutralizeCtaAttributes} runs inside DOMPurify's afterSanitizeAttributes hook. */
let neutralizeEmailPreviewCtas = false;

function neutralizeCtaAttributes(node: Element): void {
  const tag = node.nodeName.toLowerCase();

  if (tag === 'a' || tag === 'area') {
    node.setAttribute('href', '#');
    node.removeAttribute('target');
    node.removeAttribute('download');
    node.removeAttribute('ping');
    return;
  }

  if (tag === 'button') {
    node.setAttribute('type', 'button');
    node.removeAttribute('formaction');
    return;
  }

  if (tag === 'input') {
    const type = (node.getAttribute('type') || '').toLowerCase();
    if (type === 'submit' || type === 'image') {
      node.setAttribute('type', 'button');
    }
    node.removeAttribute('formaction');
  }
}

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (!neutralizeEmailPreviewCtas || node.nodeType !== 1) {
    return;
  }
  neutralizeCtaAttributes(node as Element);
});

export function processTemplateContent({
  content,
  data = {},
  errorIconSrc,
  mappedValueClass,
  errorClass,
  isHtmlContent = false,
}: ProcessTemplateOptions): string {
  // 1. Prepare Content
  // If it's SMS (plain text), we must convert newlines to <br> BEFORE processing
  // to prevent them from being lost in the final HTML render.
  const preparedContent = isHtmlContent ? content : content.replace(/\n/g, '<br/>');

  const PLACEHOLDER_REGEX = /{{\s*([^{}\n\r]+?)\s*}}/g;

  const processedHtml = preparedContent.replace(PLACEHOLDER_REGEX, (_, inner) => {
    const key = inner.trim();

    // Check if key exists in mapping data
    if (key in data) {
      const val = data[key];

      // Edge Case: Value is empty/null -> Render Error
      if (val === null || val === undefined || val === '') {
        return getErrorHtml(key, errorIconSrc, errorClass);
      }

      // DETERMINISTIC CHECK:
      // We know it is a Mapped Context Attribute if the *value itself*
      // is wrapped in double curly braces.
      if (val.trim().startsWith('{{') && val.trim().endsWith('}}')) {
        return `<span class="${mappedValueClass}">${val}</span>`;
      }

      // Default: It is a standard text value (e.g. "John")
      return val;
    }

    // No mapping found: Return original string (e.g. {{Something}})
    return `{{${key}}}`;
  });

  // 2. Sanitize final result
  // We sanitize specific tags to allow styling but prevent scripts
  try {
    neutralizeEmailPreviewCtas = Boolean(isHtmlContent);
    return DOMPurify.sanitize(processedHtml, {
      ADD_TAGS: ['style'],
      WHOLE_DOCUMENT: true,
    });
  } finally {
    neutralizeEmailPreviewCtas = false;
  }
}

function getErrorHtml(key: string, iconSrc: string, baseClass: string): string {
  return `<span title="Placeholder mapping needs to be corrected" class="${baseClass}">
            <span class="${baseClass}__text">{{${key}}}</span>
            <span class="${baseClass}__icon">
                <img src="${iconSrc}" alt="Placeholder error" />
            </span>
          </span>`;
}
